import { useEffect, useState, type FormEvent } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';

const navItems = [
  { to: '/', label: '首页', icon: 'ri-home-5-line' },
  { to: '/category', label: '电视剧分类', icon: 'ri-layout-grid-line' },
  { to: '/news', label: '影视资讯', icon: 'ri-newspaper-line' },
  { to: '/search', label: '搜索', icon: 'ri-search-line' },
  { to: '/about', label: '关于我们', icon: 'ri-information-line' },
];

const hotKeywords = ['长夜将明', '风起长安', '星海纪元', '迷雾追凶'];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value) return;
    navigate(`/search?q=${encodeURIComponent(value)}`);
    setKeyword('');
  };

  return (
    <header className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200">
      <div className="hidden md:flex items-center justify-between px-6 lg:px-10 h-9 bg-primary-950 text-background-50 text-xs tracking-wide">
        <div className="flex items-center gap-2">
          <i className="ri-fire-line text-accent-400"></i>
          <span className="text-background-200">今日热播 · 全网同步更新中</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-background-300">热搜：</span>
          <div className="flex items-center gap-3">
            {hotKeywords.map((word) => (
              <Link
                key={word}
                to={`/search?q=${encodeURIComponent(word)}`}
                className="text-background-200 hover:text-accent-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                {word}
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between gap-4 px-4 md:px-6 lg:px-10 h-16">
        <Link to="/" className="flex items-center gap-2.5 shrink-0 cursor-pointer">
          <span className="w-10 h-10 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center text-xl">
            <i className="ri-clapperboard-fill"></i>
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-lg font-bold text-foreground-950 tracking-tight">上司的老婆</span>
            <span className="text-[10px] md:text-[11px] text-foreground-500 tracking-[0.16em] mt-1">DRAMA · STORY · NEWS</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `px-3.5 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  isActive
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-foreground-700 hover:text-primary-600 hover:bg-background-100'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <form onSubmit={handleSearch} className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索剧集或资讯"
              className="w-48 lg:w-56 pl-9 pr-3 py-2 text-sm rounded-md bg-background-100 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition"
            />
          </form>
        </div>

        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          aria-label="打开导航菜单"
          className="md:hidden w-10 h-10 flex items-center justify-center rounded-md text-foreground-800 hover:bg-background-100 cursor-pointer text-xl"
        >
          <i className={open ? 'ri-close-line' : 'ri-menu-line'}></i>
        </button>
      </div>

      {open ? (
        <div className="md:hidden border-t border-background-200 bg-background-50 animate-fade-in">
          <div className="px-4 py-4 space-y-3">
            <form onSubmit={handleSearch} className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索剧集或资讯"
                className="w-full pl-9 pr-3 py-2.5 text-sm rounded-md bg-background-100 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300"
              />
            </form>
            <div className="grid grid-cols-2 gap-2">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                      isActive ? 'bg-primary-100 text-primary-700' : 'bg-background-100 text-foreground-700'
                    }`
                  }
                >
                  <i className={item.icon}></i>
                  {item.label}
                </NavLink>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <span className="text-xs text-foreground-500">热搜</span>
              {hotKeywords.map((word) => (
                <Link
                  key={word}
                  to={`/search?q=${encodeURIComponent(word)}`}
                  className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs cursor-pointer whitespace-nowrap"
                >
                  {word}
                </Link>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </header>
  );
}